import React, { useState, lazy, Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  LOGIN,
  VALIDATE_EMAIL,
  LINK_USERS,
  LINK_ADDITIONAL_USER,
  LINK_USER_TO_DOCUMENT,
  UPDATE_USER,
} from "../../graphQL/mutations";
import Auth from "../../utils/auth";
import {
  Alert,
  Collapse,
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Link,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ForgotPassword from "../../components/password/ForgotPassword";
import HeaderLogo from "../../components/header/HeaderLogo";
import CircularLoading from "../../components/loading/CircularLoading";
import OrionSSO from "../../components/sso/OrionSSO";
import DelayedFallback from "../../components/loading/DelayedFallback";
import * as Sentry from "@sentry/react";
import ExtractSubdomain from "../../utils/helpers/ExtractSubdomain";

const CannotLinkModal = lazy(() => import("../../components/donor/modals/CannotLinkModal"));

export default function Login(props) {
  const [formState, setFormState] = useState({ email: "", password: "" });
  const [passwordIsMasked, togglePasswordMask] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [login, { error }] = useMutation(LOGIN);
  const [validateEmail, { validateEmailError }] = useMutation(VALIDATE_EMAIL);
  const [linkUsers, { linkUsersError }] = useMutation(LINK_USERS);
  const [updateUser, { updateUserError }] = useMutation(UPDATE_USER);
  const [linkAdditionalUser, { linkAdditionalUserError }] = useMutation(LINK_ADDITIONAL_USER);
  const [linkUserToFirm, { advisorToFirmError }] = useMutation(LINK_USER_TO_DOCUMENT);
  const [loadingSignIn, setLoadingSignIn] = useState(false);

  const [passwordResetRequired, setPasswordResetRequired] = useState(false);

  var currentURL = new URL(window.location.href);
  var searchParams = new URLSearchParams(currentURL.search);

  const hashParams = new URLSearchParams(window.location.hash.slice(1)); // remove the '#' and parse

  const navigate = useNavigate();

  let tokenType = searchParams.get("tokenType");
  let emailToken = searchParams.get("token");

  let token;
  let refreshToken;
  let userLoginId;
  const subdomain = ExtractSubdomain(window.location.hostname);
  const source = searchParams.get("source") || (subdomain !== "portal" ? subdomain : undefined);
  let linkSuccess = false;

  const handleEmailLinking = async (userLoginId) => {
    //if the url included a token and tokenType, attempt to validate the email
    if (tokenType && emailToken) {
      console.log("tokenType and emailToken exist");
      console.log("tokenType", tokenType);
      console.log("emailToken", emailToken);
      if (tokenType === "donorLinkingAdvisor" || tokenType === "advisorLinkingDonor") {
        console.log("attempting to link users");
        try {
          const linkUserResponse = await linkUsers({
            variables: {
              token: emailToken,
              userLoginId: userLoginId,
            },
          });

          console.log("successful linkUsers, response:", linkUserResponse.data.linkUsers);
        } catch (e) {
          console.log("Failed to link users", e);
          throw new Error("Failed to link users:", e);
        }
      } else if (tokenType === "additionalUser") {
        // logic to handle addtionalUser link
        console.log("attempting to link additional user");
        try {
          const linkUserResponse = await linkAdditionalUser({
            variables: {
              token: emailToken,
              userLoginId: userLoginId,
            },
          });
          console.log(
            "successful linkAdditionalUser, response:",
            linkUserResponse.data.linkAdditionalUser
          );
        } catch (e) {
          console.log("Failed to link additional users");
          throw new Error("Failed to link additional users:", e);
        }
      } else if (
        tokenType === "firmUserLinkingAdvisor" ||
        tokenType === "firmUserLinkingFirmUser" ||
        tokenType === "partnerUserLinkingPartnerUser"
      ) {
        console.log("attempting to link user to a firm");
        try {
          const linkUserResponse = await linkUserToFirm({
            variables: {
              token: emailToken,
              userLoginId: userLoginId,
            },
          });
          linkSuccess = true;
          console.log("successful link user to firm, response:", linkUserResponse);
        } catch (e) {
          console.log("Failed to link user to firm", e);
          throw new Error("Failed to link user to firm:", e);
        }
      } else {
        console.log("attempting to validate email");
        try {
          const validateEmailResponse = await validateEmail({
            variables: {
              email: formState.email,
              emailToken: emailToken,
              tokenType: tokenType,
            },
          });

          token = validateEmailResponse.data.validateEmail.accessToken;
          console.log("successful validateEmail, token:", token);
        } catch (e) {
          console.error("validateEmail error:", e);
          console.error("validateEmail graphql error", validateEmailError);
          setErrorMessage("Incorrect email or password");
          throw new Error("Error on validateEmail:", e, validateEmailError);
        }
      }
      if (source) {
        try {
          const mutationReponse = await updateUser({
            variables: {
              userId: userLoginId,
              args: {
                baseUser: {
                  meta: {
                    source: source,
                  },
                },
              },
            },
          });
        } catch (e) {
          console.error("updateUser error:", e);
          console.error("updateUser graphql error", updateUserError);
          throw new Error("Error on updateUser:", e, updateUserError);
        }
      }
    }

    // console.log("token", token);
    console.log("logging in with token:", token);
    Auth.login(token, refreshToken);
    // if verified email, navigate to dashboard

    //Logging in through token -- successful link
    if (Auth.checkEmailVerified()) {
      setErrorMessage("");
      // As of current, alerts only setup for: 1. firmUserLinkingAdvisor 2. firmUserLinkingFirmUser
      if (
        tokenType === "firmUserLinkingAdvisor" ||
        tokenType === "firmUserLinkingFirmUser" ||
        tokenType === "partnerUserLinkingPartnerUser"
      ) {
        window.location.assign("/dashboard" + `?linkSuccess=${linkSuccess}`);
      } else {
        window.location.assign("/");
      }
    }
    // if not verified email, navigate to email verification page
    else {
      console.log("Email is not verified");
      navigate("/verify-reminder");
    }
  };

  // Function to handle token extraction and storage from Orion SSO
  const handleTokenStorage = async () => {
    // Extract tokens from URL hash
    token = hashParams.get("accessToken");
    refreshToken = hashParams.get("refreshToken");
    userLoginId = hashParams.get("userLoginId");

    if (token && refreshToken) {
      Auth.login(token, refreshToken);
      // Clear the hash after storing the tokens to clean up the URL
      const newUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, document.title, newUrl);

      await handleEmailLinking(userLoginId);
    }
  };

  useEffect(() => {
    handleTokenStorage();
  }, [hashParams]);

  const handleFormSubmit = async (event) => {
    setLoadingSignIn(true);
    event.preventDefault();

    console.log("emailToken", emailToken);
    console.log("tokenType", tokenType);

    // Attempt to login
    try {
      console.log("attempting to login");
      const mutationResponse = await login({
        variables: {
          email: formState.email.trim(),
          password: formState.password.trim(),
        },
      });
      //TODO check if login is successful display message if failed
      if (mutationResponse.data.login?.status === "passwordResetRequired") {
        console.log("Password reset required");
        setPasswordResetRequired(true);
      }
      if (mutationResponse.data.login?.status === "incorrectCredentials") {
        console.log("incorrectCredentials");
        setErrorMessage("Incorrect email or password");
        setLoadingSignIn(false);
        return;
      }
      if (mutationResponse.data.login?.status === "accountLocked") {
        console.log("accountLocked");
        setErrorMessage('Account Locked. Please click "Forgot Password?"');
        setLoadingSignIn(false);
        return;
      }
      if (mutationResponse.data.login?.status === "deactivated") {
        console.log("deactivated");
        setErrorMessage("Account deactivated. Please contact support");
        setLoadingSignIn(false);
        return;
      }

      token = mutationResponse.data.login.accessToken;
      refreshToken = mutationResponse.data.login.refreshToken;
      userLoginId = mutationResponse.data.login.user._id;
      console.log("successful login, token:", token);

      if (
        !mutationResponse.data.login.user?.meta?.testData &&
        mutationResponse.data.login.user?.type !== "internal"
      ) {
        const userRoles = mutationResponse.data.login.user.roles.join(", "); // Example: "donor, advisor"

        Sentry.setUser({
          id: mutationResponse.data.login.user._id,
          email: formState.email.trim(),
          userType: userRoles,
          subdomain: ExtractSubdomain(window.location.hostname),
        });

        Sentry.captureMessage("User logged in", {
          level: "info",
          tags: {
            eventType: "login",
            userType: userRoles,
            subdomain: ExtractSubdomain(window.location.hostname),
          },
          extra: {
            userId: mutationResponse.data.login.user._id,
            email: formState.email.trim(),
            userType: userRoles,
            subdomain: ExtractSubdomain(window.location.hostname),
          },
        });
      }
    } catch (e) {
      console.error("login error:", e);
      console.error("login graphql error", error);
      setErrorMessage("Incorrect email or password");
      setLoadingSignIn(false);
      return;
    }
    Auth.login(token, refreshToken);

    await handleEmailLinking(userLoginId);
    setLoadingSignIn(false);
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    setFormState({
      ...formState,
      [id]: value,
    });
  };

  const handleClearError = (event) => {
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const styles = {
    marginAndWidth: {
      my: 1,
      display: "flex",
    },
  };

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mt: -10,
      }}
    >
      {tokenType && emailToken && (
        <Suspense fallback={<DelayedFallback />}>
          <CannotLinkModal emailToken={emailToken} tokenType={tokenType} />
        </Suspense>
      )}
      <Box
        sx={{
          minWidth: "300px",
          width: `${screenWidth * 0.2}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <HeaderLogo boxSx={{ height: { xs: "110px", sm: "110px", md: "110px", lg: "110px" } }} />
        <Typography variant="body2" sx={{ mb: 2, mt: 0.5 }}>
          <span style={{ opacity: "0.7" }}>Don't have an account?</span>
          <Link
            id="createAccount"
            underline="always"
            sx={{ color: "secondary.main", ml: "4px", cursor: "pointer" }}
            onClick={() => {
              if (emailToken && tokenType) {
                navigate({
                  pathname: `/create-account`,
                  search: emailToken ? `?token=${emailToken}&tokenType=${tokenType}` : "",
                });
              } else {
                navigate({
                  pathname: `/select-account-type`,
                });
              }
            }}
          >
            Sign Up Here
          </Link>
        </Typography>
        <form
          onSubmit={handleFormSubmit}
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <TextField
            variant="outlined"
            required
            InputProps={{ sx: { backgroundColor: "white" } }}
            sx={{ backgroundColor: "white", ...styles.marginAndWidth, width: "100%" }}
            label="Email"
            id="email"
            placeholder="Email"
            // value={email}
            onChange={handleChange}
            onClick={handleClearError}
          />
          <TextField
            variant="outlined"
            required
            sx={{ ...styles.marginAndWidth, backgroundColor: "white" }}
            type={passwordIsMasked ? "password" : "text"}
            label="Password"
            id="password"
            placeholder="Password"
            // value={password}
            onChange={handleChange}
            onClick={handleClearError}
            InputProps={{
              sx: {
                backgroundColor: "white",
              },
              endAdornment: (
                <InputAdornment position="end">
                  {passwordIsMasked ? (
                    <Tooltip
                      title="Show Password"
                      sx={{ ":hover": { backgroundColor: "lightGray" } }}
                    >
                      <IconButton
                        onClick={() => {
                          togglePasswordMask(!passwordIsMasked);
                        }}
                      >
                        <VisibilityOffIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="Hide Password"
                      sx={{ ":hover": { backgroundColor: "lightGray" } }}
                    >
                      <IconButton
                        onClick={() => {
                          togglePasswordMask(!passwordIsMasked);
                        }}
                      >
                        <RemoveRedEyeIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", mt: 2, mb: 2 }}>
            <ForgotPassword passwordResetRequired={passwordResetRequired} />
          </Box>
          <Button
            id="loginSubmit"
            variant="Button1"
            type="submit"
            sx={{ width: "100%" }}
            disabled={loadingSignIn}
          >
            Sign In
            {loadingSignIn && (
              <CircularLoading loaderSx={{ position: "absolute" }} loading={false} size={24} />
            )}
          </Button>
          <Collapse in={!!errorMessage}>
            <Box sx={{ display: "flex", mt: 2, mb: 1 }}>
              <Alert
                severity="error"
                sx={{
                  width: "inherit",
                  display: "flex",
                  wordBreak: "break-word",
                  width: "100%",
                  border: "1px solid red",
                }}
              >
                {errorMessage}
              </Alert>
            </Box>
          </Collapse>
          <Box sx={{ ...styles.marginAndWidth, width: "100%" }}>
            <OrionSSO emailToken={emailToken} tokenType={tokenType} />
          </Box>
        </form>
      </Box>
    </Box>
  );
}
